import type { ZodType } from 'zod';
import { AgentConfig } from '../models/agent-config.model';

/** One turn already stored in a conversation's server-side history. */
export interface AiConversationItem {
  /** The conversation item id (`msg_…`). Feedback is keyed on this — it is the one id a turn
   *  carries both when it is sent and when it is restored. */
  id: string;
  role: string;
  text: string;
}

/** Token counts for one model turn, as reported by the Responses API `usage` block. */
export interface TokenUsage {
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  /** Input tokens served from the prompt cache; a subset of `inputTokens`. */
  cachedInputTokens?: number;
  /** Output tokens spent on reasoning; a subset of `outputTokens`. */
  reasoningTokens?: number;
}

/**
 * Feedback-correlation metadata for the most recently completed turn: the final Responses API
 * `response.id` (a user question can span several `responses.create` hops via the tool loop —
 * this is the LAST one) and the token usage summed across every hop of that turn.
 */
export interface TurnMeta {
  /** Conversation item id of the assistant message this turn produced — what the feedback footer
   *  rates, and the one id the turn also carries when it is restored. */
  itemId?: string;
  responseId?: string;
  usage: TokenUsage;
}

/**
 * Correlation for one completed turn, used to attach a rep's thumbs vote to the agent's trace as a
 * Foundry annotation. The client sends a W3C `traceparent` on the agent call and Foundry adopts its
 * trace id as the trace's `operation_Id`, so the client knows that id and stamps it on the feedback
 * event — which is what lands the annotation on the trace (`gen_ai.response.id` alone only enables a
 * KQL join, not the portal attach).
 */
export interface TurnCorrelation {
  /** Conversation item id of the assistant message this turn produced — the ledger's key. */
  itemId: string;
  /** Conversation the turn belongs to; scopes the ledger doc. */
  conversationId: string;
  responseId: string;
  /** W3C trace id (32 hex) the client sent in the traceparent → the trace's `operation_Id`. */
  traceId: string;
  agentId?: string;
  agentName?: string;
  agentVersion?: string;
  /** Token counts for the turn, summed across any tool-loop hops. */
  usage?: TokenUsage;
  /** Foundry project endpoint the turn ran on; selects the App Insights resource its feedback goes to. */
  endpoint?: string;
}

/** Sink the transport calls once per completed turn to register a {@link TurnCorrelation}. */
export type TurnCorrelationRecorder = (correlation: TurnCorrelation) => void;

/** How a turn ended: `failure` covers an error, a timeout, or a cancellation by the system. */
export type TurnOutcome = 'success' | 'failure';

/** One finished turn, written as the turn's `invoke_agent` span. */
export interface TurnUsageRecord {
  conversationId: string;
  /** The final `response.id` of the turn; absent when the service returned none. */
  responseId?: string;
  /** W3C trace id (32 hex) the client sent in the traceparent; the span's trace id and operation id. */
  traceId: string;
  /** W3C parent id (16 hex) the client sent in the traceparent; the span's parent span id. */
  parentSpanId: string;
  agentName: string;
  agentVersion?: string;
  /**
   * The app surface that requested the turn, as the caller named it in the `origin` structured input
   * (for example `goapp-voice-note-detection`); absent when the caller named none.
   */
  origin?: string;
  /** Model deployment that answered, as reported by the response. */
  model?: string;
  /** Token counts for the turn, summed across the tool-loop hops that returned; zero when none did. */
  usage: TokenUsage;
  /** Wall-clock time the turn started, in milliseconds since the Unix epoch. */
  startTime: number;
  /** Milliseconds from the start of `send()` to the final reply or the failure. */
  durationMs: number;
  outcome: TurnOutcome;
  /**
   * Low-cardinality class of the failure, per the OpenTelemetry `error.type` attribute: the HTTP status
   * code when the service returned one, else the error name, else `_OTHER`. Present on failure only.
   */
  errorType?: string;
  /** The rep's message for the turn; absent when the payload carries no user text. */
  question?: string;
  /** The assistant's final reply text, without tool calls or tool results; absent on failure. */
  answer?: string;
}

/** Sink the transport calls once per finished turn, whether it succeeded, failed, or produced no message. */
export type TurnUsageRecorder = (turn: TurnUsageRecord) => void;

/**
 * Limits for the requests of one turn, applied to each request the turn makes, tool-loop follow-ups
 * included. A field left unset keeps the transport's default.
 */
export interface AiRequestOptions {
  /** Longest wait for one request, in milliseconds; a request still unanswered then fails with a timeout. */
  timeoutMs?: number;
  /** How many times a failed request is resent before the turn fails. */
  maxRetries?: number;
  /** Aborts every request of the turn, including the read of a response body; the turn then fails with an abort error. */
  signal?: AbortSignal;
}

/**
 * A live, multi-turn conversation with an AI agent. The agent retains context
 * server-side across calls, so callers only pass the new turn's payload.
 */
export interface AiConversation {
  /** Host-side conversation id. */
  readonly id: string;
  /**
   * Metadata for the turn `send()`/`parse()` most recently completed on this conversation, for
   * feedback correlation + token display — `null` before any turn has been sent (e.g. a
   * freshly-resumed conversation whose history was only read via `listItems()`). Sends are
   * sequential per conversation, so one slot is always unambiguous.
   */
  readonly lastTurn: TurnMeta | null;
  /**
   * Sends one turn and resolves with the agent's raw output text. Callers parse
   * it into a domain shape (the transport stays agnostic of payload schemas).
   * `options` limits each request of the turn; without it the transport's defaults apply.
   */
  send(payload: unknown, options?: AiRequestOptions): Promise<string>;
  /**
   * Sends one turn requesting structured output conforming to `schema` (a zod
   * schema), and resolves with the parsed result. Used for the analyze turn.
   */
  parse<TResult>(payload: unknown, schema: unknown, schemaName: string): Promise<TResult>;
  /** Returns the conversation's stored turns (server-side history). */
  listItems(): Promise<AiConversationItem[]>;
  /**
   * Releases the local handle WITHOUT deleting the server-side conversation.
   * Non-destructive — the conversation remains resumable (this is what callers
   * want when navigating away or starting a new thread).
   */
  close(): Promise<void>;
  /** Permanently deletes the host-side conversation. Destructive; use only when
   *  intentionally discarding (re-analyze, or an explicit delete action). */
  delete(): Promise<void>;
  /**
   * Attaches structured inputs (e.g. summary `context`) to the NEXT request.
   * The transport merges them into the `responses.create` body under the
   * `structured_inputs` key, letting the hosted agent read them via
   * `{{structured_inputs.context}}` in its prompt template.
   */
  setStructuredInputs(inputs: Record<string, unknown>): void;
  /**
   * Exposes a subset of registered `@AITool` client tools to the model for
   * EVERY request of this conversation (tool-loop follow-ups included).
   * Direct-model conversations only — hosted agents carry their tool
   * catalogue on the agent definition. Unknown names are skipped.
   */
  setRequestTools(toolNames: string[]): void;
  /**
   * Requires every reply of this conversation to be JSON matching `schema` (a zod
   * schema), which the model enforces as strict structured output. Direct-model
   * conversations only — hosted agents carry their output format on the agent
   * definition.
   */
  setResponseFormat(schemaName: string, schema: ZodType): void;
}

/** A conversation the host keeps for the signed-in user, as the host's conversation list returns it. */
export interface AiConversationSummary {
  /** Host-side conversation id. */
  id: string;
  /** When the host created the conversation, ISO 8601. */
  createdAt: string;
  /** The metadata stamped on the conversation when it was started; empty when none was. */
  metadata: Record<string, string>;
  /** True when the user deleted the conversation from their history; the host still keeps it for audit. */
  deleted: boolean;
}

/**
 * Transport abstraction for invoking an AI agent. Implementations route by
 * `AgentHost`. Today only Foundry; future hosts (Azure OpenAI direct, OpenAI)
 * implement this same interface.
 */
export interface AiTransport {
  /** Opens a new host-side conversation, optionally stamping metadata (title,
   *  origin, kind marker) onto it at creation. `options` limits the create request;
   *  without it the transport's defaults apply. */
  startConversation(agent: AgentConfig, metadata?: Record<string, string>, options?: AiRequestOptions): Promise<AiConversation>;
  /** Re-attaches to an existing host-side conversation by id (no new conversation created). */
  resumeConversation(agent: AgentConfig, conversationId: string): Promise<AiConversation>;
  /** Deletes a conversation from the user's history by id (no live handle required). The host marks it deleted
   *  and keeps it, items included, for audit; {@link listConversations} reports it with `deleted` set. */
  deleteConversation(agent: AgentConfig, conversationId: string): Promise<void>;
  /** The signed-in user's conversations with the agent, most recently created first, deleted ones included. The
   *  host returns only the caller's own conversations, so no filter by user is needed. The list is read page by
   *  page and ends after the page on which `enough` holds for the conversations read so far, or at the end of the
   *  user's conversations; without `enough`, every conversation is read. */
  listConversations(agent: AgentConfig, enough?: (read: AiConversationSummary[]) => boolean): Promise<AiConversationSummary[]>;
}
